import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, Col, Descriptions, Form, Input, Row, Tag, Typography, notification } from 'antd';
import { LockOutlined } from '@ant-design/icons';

import { useAuth } from '../../authentication/AuthProvider';
import request from '../../lib/request';
import { label } from '../../lib/format';

const ROLE_LABELS = {
  SYSTEM_ADMIN: 'System Administrator',
  COMPANY_MANAGER: 'Manager',
  COMPANY_USER: 'Staff',
  CLIENT_ADMIN: 'Client portal',
};

/** My account: who I am signed in as, and the self-service password change (also the forced first-login change). */
export default function AccountPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const required = params.get('required') === '1' || auth.user?.mustChangePassword;
  const [form] = Form.useForm();
  const [saving, setSaving] = React.useState(false);

  const onFinish = (values) => {
    setSaving(true);
    request
      .post('/auth/change-password', { current_password: values.current_password, new_password: values.new_password })
      .then((r) => r.json())
      .then((r) => {
        if (r.status) {
          auth.signIn({ ...auth.user, ...r.data });
          notification.success({ message: 'Password changed', description: 'Your other sessions have been signed out.' });
          form.resetFields();
          if (required) navigate('/', { replace: true });
        } else {
          notification.error({ message: r.message });
        }
      })
      .finally(() => setSaving(false));
  };

  return (
    <div>
      <Typography.Title level={4} style={{ marginBottom: 16 }}>
        My account
      </Typography.Title>
      {required && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Please choose your own password"
          description="You are signed in with a temporary password. Set a new one to continue to the system."
        />
      )}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={10}>
          <Card title="Signed in as">
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Name">{auth.user?.name}</Descriptions.Item>
              <Descriptions.Item label="Email">{auth.user?.email}</Descriptions.Item>
              <Descriptions.Item label="Role">
                <Tag>{ROLE_LABELS[auth.user?.role] || label(auth.user?.role)}</Tag>
              </Descriptions.Item>
              {auth.user?.companyName && <Descriptions.Item label="Company">{auth.user.companyName}</Descriptions.Item>}
            </Descriptions>
            <Typography.Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0 }}>
              Name and email are maintained by your administrator under Staff / Users.
            </Typography.Paragraph>
          </Card>
        </Col>
        <Col xs={24} lg={14}>
          <Card title="Change password">
            <Form form={form} layout="vertical" onFinish={onFinish} style={{ maxWidth: 420 }}>
              <Form.Item name="current_password" label="Current password" rules={[{ required: true, message: 'Enter your current password' }]}>
                <Input.Password prefix={<LockOutlined />} autoComplete="current-password" />
              </Form.Item>
              <Form.Item
                name="new_password"
                label="New password"
                extra="At least 8 characters, with both a letter and a digit."
                rules={[
                  { required: true, message: 'Enter a new password' },
                  { min: 8, message: 'At least 8 characters' },
                  { pattern: /^(?=.*[A-Za-z])(?=.*\d).+$/, message: 'Use both a letter and a digit' },
                ]}
              >
                <Input.Password prefix={<LockOutlined />} autoComplete="new-password" />
              </Form.Item>
              <Form.Item
                name="confirm_password"
                label="Repeat new password"
                dependencies={['new_password']}
                rules={[
                  { required: true, message: 'Repeat the new password' },
                  ({ getFieldValue }) => ({
                    validator: (_, value) => (!value || getFieldValue('new_password') === value ? Promise.resolve() : Promise.reject(new Error('The two passwords do not match'))),
                  }),
                ]}
              >
                <Input.Password prefix={<LockOutlined />} autoComplete="new-password" />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={saving}>
                Change password
              </Button>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
